import { AbsgArrayHelper } from '@absg/angular-common';
import { PaginationControlsComponent } from '@absg/angular-components';
import { AuthorizationService } from '@absg/angular-platform';
import {
  DispenseTypes, FormularyType, ManagePosition, RuleContainerSetting, RuleHelper, Rules, RuleService, StringConstants,
} from '@absg/nim-client-shared';
import { RuleContainerSetting as ApiRuleContainerSetting } from '@absg/api-station-management';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { StationInfoService } from 'core.services/station-info.service';
import { ManagePositionsService } from 'inventory.root/shared/services/manage-positions.service';
import { PaginationInstance } from 'ngx-pagination';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { concatMap, map, take, takeUntil, tap } from 'rxjs/operators';
import { BaseRuleContainerComponent } from 'shared.components/rules/base-rule-container/base-rule-container.component';
import { InventoryManagementPermission } from 'shared.root/permissions/inventory-management.permission';
import { InventoryService } from 'shared.root/services/inventory.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush, // to fix expression has changed after it was checked error
  selector: '[nc-rule-container]',
  templateUrl: './rule-container.component.html',
  standalone: false,
})
export class RuleContainerComponent extends BaseRuleContainerComponent implements OnInit, OnDestroy {
  pagingConfig: PaginationInstance = PaginationControlsComponent.getDefaultConfig();
  originalRules: RuleContainerSetting[];
  rules: RuleContainerSetting[];
  filteredRules: RuleContainerSetting[];
  containerSettingsForm: UntypedFormGroup = new UntypedFormGroup({});
  isEditAuthorized = true;
  // TODO: Split rule container
  ruleCategories = ['item settings', 'properties'];
  isPracticeOwnedItem = false;
  isPatientOwnedItem = false;
  selectedPosition: ManagePosition;
  private hasEditWitnessPermission = false;
  private componentIsDestroyed$ = new Subject<void>();

  constructor(private ruleService: RuleService,
    private formBuilder: UntypedFormBuilder,
    private managePositionsService: ManagePositionsService,
    private authorizationService: AuthorizationService,
    private inventoryService: InventoryService,
    private stationInfoService: StationInfoService,
  ) {
    super();

    this.pagingConfig.itemsPerPage = 7;
    this.pagingConfig.id = 'ruleContainerPagingConfig';
  }

  ngOnInit(): void {
    this.getEditWitnessPermission();
    this.ruleService.selectedContainerRules.pipe(takeUntil(this.componentIsDestroyed$)).subscribe(containerRules => {
      this.originalRules = containerRules;
      this.pagingConfig.currentPage = 1;
      this.rules = AbsgArrayHelper.cloneDeep(containerRules) as RuleContainerSetting[];

      this.managePositionsService.getSelectedManagePosition()
        .pipe(take(1), takeUntil(this.componentIsDestroyed$))
        .subscribe(position => {
          this.selectedPosition = position;
          this.isPracticeOwnedItem = position.productDetail.formularyTypeId === FormularyType.PracticeOwnedItem;
          this.isPatientOwnedItem = !AbsgArrayHelper.isNil(position.patientInfo);
          this.filteredRules = this.filterAndSortItemSettingRules(this.rules);
          this.initializeFormAndValidation();
          this.setDisplayValues();
        });
    });
    this.isSaveEnabled = new BehaviorSubject<boolean>(false);
    this.isDirty = new BehaviorSubject<boolean>(false);
  }

  ngOnDestroy(): void {
    this.componentIsDestroyed$.next();
    this.componentIsDestroyed$.complete();
  }

  filterAndSortItemSettingRules(rules: RuleContainerSetting[]): RuleContainerSetting[] {
    let filteredRules = new Array<RuleContainerSetting>();
    if (rules) {
      for (const rule of this.rules) {
        if (this.ruleIsBool(rule) &&
          this.ruleCategories.includes(rule.rule.ruleCategory.name.toLocaleLowerCase())) {
          if (!this.isPracticeOwnedItem || (this.isPracticeOwnedItem && rule.rule.ruleId !== Rules.positionLevelRules.autoReorder)) {
            if (rule.rule.ruleId === Rules.positionLevelRules.witnessRequired) {
              if (this.hasEditWitnessPermission) {
                filteredRules.push(rule);
              }
            } else {
              filteredRules.push(rule);
            }
          }
        }
      }
    }
    filteredRules = filteredRules.filter(item => item.ruleId !== Rules.stationLevelRules.billingRequired);
    if (this.isPatientOwnedItem) {
      filteredRules = filteredRules.filter(item => item.ruleId !== Rules.positionLevelRules.autoReorder);
    }
    return this.sortList(filteredRules);
  }

  setDisplayValues(): void {
    if (this.rules && this.rules.length > 0) {
      for (const rule of this.rules) {
        if (this.ruleIsBool(rule)) {
          rule.booleanValue = rule.value.toLocaleUpperCase().localeCompare(StringConstants.boolY) === 0;
        }
        rule.originalValue = rule.value;
      }
    }
  }

  ruleIsBool(rule: RuleContainerSetting): boolean {
    return rule.rule.dataType && rule.rule.dataType.toLocaleLowerCase().localeCompare('bool') === 0;
  }

  getFormControlName(rule: RuleContainerSetting): string {
    // Need to include containerId in the form control name/id incase this component is loaded more
    // than once on the same form for different containers
    return `rule-${rule.containerId}-${rule.ruleId}`;
  }

  getFormControlSliderWrapperName(rule: RuleContainerSetting): string {
    return `slider-wrapper-${rule.containerId}-${rule.rule.name.replace(/ /g, '')}`;
  }

  isRulesDirty(rules: RuleContainerSetting[],
    ruleChanged: RuleContainerSetting = null,
    changedValue: string = null,
  ): boolean {
    let isDirty = false;

    if (this.rules && this.rules.length > 0) {
      for (const rule of rules) {
        if (rule.originalValue) {
          const oldValue: string = rule.originalValue.toString();
          let newValue: string = rule.value ? rule.value.toString() : '';
          if (ruleChanged !== null && rule.ruleId === ruleChanged.ruleId) {
            newValue = changedValue;
          }
          isDirty = isDirty || (newValue !== oldValue);
          if (isDirty) {
            break;
          }
        }
      }
    }
    return isDirty;
  }

  onSave(): Observable<boolean> {
    const rulesToSave = this.filteredRules.filter(rule => rule.originalValue && rule.originalValue !== rule.value);
    // new Observable()
    const witnessRule = AbsgArrayHelper.find(rulesToSave, rule => rule.ruleId === Rules.stationLevelRules.witnessRequired);
    if (witnessRule != null) {
      const verifyQoh = AbsgArrayHelper.find(this.filteredRules, rule => rule.ruleId === Rules.stationLevelRules.verifyQOH);
      verifyQoh.value = witnessRule.value;
      rulesToSave.push(verifyQoh);
    }
    const propertiesRulesToSave = rulesToSave.filter(rule => rule.rule.ruleCategory.name.toLocaleLowerCase() === 'properties');
    const otherRulesToSave = rulesToSave.filter(rule => rule.rule.ruleCategory.name.toLocaleLowerCase() !== 'properties');

    return forkJoin([
      (otherRulesToSave.length > 0) ? this.ruleService.updateRulesForPosition(otherRulesToSave) : of([] as ApiRuleContainerSetting[]),
      (propertiesRulesToSave.length > 0) ? this.ruleService.updatePropertyRulesForPosition(propertiesRulesToSave) : of([] as ApiRuleContainerSetting[]),
    ]).pipe(
      concatMap((results => {
        const autoReorderRule = AbsgArrayHelper.find(rulesToSave, rule => rule.ruleId === Rules.stationLevelRules.autoReorder);
        if (autoReorderRule != null) {
          return this.handleAutoReorderSettingChange(autoReorderRule)
            .pipe(take(1), map(() => results));
        }
        return of(results);

      })),
      concatMap(([updatedRulesForPosition, updatedPropertyRules]) => this.processSaveResults(updatedRulesForPosition, [updatedPropertyRules[1]], otherRulesToSave, propertiesRulesToSave)));
  }

  processSaveResults(
    resultOtherRulesUpdate: ApiRuleContainerSetting[], resultPropertiesUpdate: ApiRuleContainerSetting[],
    otherRulesToSave: RuleContainerSetting[], propertiesRulesToSave: RuleContainerSetting[],
  ): Observable<boolean> {
    return this.cleanUpInventory(otherRulesToSave).pipe(
      take(1),
      map(() => {
        // TODO: next two can be removed once cache is removed(Will speed up Manage positions)
        if ((otherRulesToSave.length > 0) && resultOtherRulesUpdate) {
          this.updateLocalCache('other');
        }
        if ((propertiesRulesToSave.length > 0) && resultPropertiesUpdate) {
          this.updateLocalCache('properties');
        }
        return !!resultOtherRulesUpdate && !!resultPropertiesUpdate;
      }),
    );
  }

  updateLocalCache(filterName: string): void {
    if (filterName === 'properties') {
      for (const originalRule of this.originalRules) {
        const matchedRule: RuleContainerSetting = this.filteredRules.find(rule =>
        (rule.containerId === originalRule.containerId
          && rule.rule.ruleCategory.name.toLocaleLowerCase() === 'properties'
          && rule.ruleId === originalRule.ruleId));
        if (matchedRule) {
          originalRule.value = matchedRule.value;
        }
      }
    } else {
      for (const originalRule of this.originalRules) {
        const matchedRule: RuleContainerSetting = this.filteredRules.find(rule =>
        (rule.containerId === originalRule.containerId
          && rule.rule.ruleCategory.name.toLocaleLowerCase() !== 'properties'
          && rule.ruleId === originalRule.ruleId));
        if (matchedRule) {
          originalRule.value = matchedRule.value;
        }
      }
    }
  }

  trackItem(__: number, item: RuleContainerSetting): number {
    if (!item) { return undefined; }

    return item.ruleId;
  }

  handleAutoReorderSettingChange(autoReorderRule: RuleContainerSetting): Observable<void> {
    const autoReorderFlag = RuleHelper.getBooleanFromRuleSetting(autoReorderRule);
    if (this.selectedPosition.isBinned() && !this.selectedPosition.isPracticeStatus()) {
      const containerId = this.selectedPosition.getContainerId();
      return this.managePositionsService.getAutoReorderItemIdForTransactionRequest(containerId,
        this.selectedPosition.productDetail.productId, this.selectedPosition.productDetails)
        .pipe(take(1),
          concatMap(autoReorderProductDetail => this.managePositionsService.saveInventoryEventAsync(true, false, true,
            autoReorderFlag, null, null, autoReorderProductDetail)
            .pipe(take(1))));
    } else {
      return this.managePositionsService.saveInventoryEventAsync(true, false, false, autoReorderFlag, null,
        null, null)
        .pipe(take(1));
    }
  }

  showWitnessAlert(rule: RuleContainerSetting): boolean {
    const control = this.containerSettingsForm.controls[this.getFormControlName(rule)];
    if (rule.ruleId === Rules.positionLevelRules.witnessRequired) {
      if (this.checkUnderHoodQty() && !rule.isDisabled) {
        rule.isDisabled = true;
        control.disable();
        return this.checkUnderHoodQty();
      }
    }
    return false;
  }

  checkUnderHoodQty(): boolean {
    const returnToUnderHoodDispenseType = this.selectedPosition.dispenseType === DispenseTypes.ReturnToUnderHood;
    return AbsgArrayHelper.some(this.selectedPosition.inventoryItems, inv =>
      AbsgArrayHelper.some(inv.inventoryItemProducts, x => x.lotQuantityUnderhood > 0)) || returnToUnderHoodDispenseType;
  }

  private cleanUpInventory(otherRulesToSave: RuleContainerSetting[]): Observable<void> {
    if (otherRulesToSave.length > 0) {
      const positionLotTrackingRule = otherRulesToSave.filter(rule => rule.ruleId === Rules.stationLevelRules.lotTracking &&
        rule.originalValue !== rule.value &&
        rule.value.toLowerCase() === 'n');
      if (positionLotTrackingRule.length > 0) {
        return this.inventoryService.sendRefreshInventoryCacheRequestAsync(positionLotTrackingRule[0].containerId)
          .pipe(tap((isSuccessful: boolean) => {
            if (isSuccessful) {
              return this.stationInfoService.refreshInventoryCacheAsync();
            } else {
              return of(null);
            }
          }),
            map(() => { }),
          );
      }
    }

    return of({}).pipe(map(() => { }));
  }

  private initializeFormAndValidation(): void {
    const group = {};
    for (const rule of this.filteredRules) {
      if (rule.rule.isRequired) {
        if (rule.rule.dataType.toLocaleLowerCase().localeCompare('bool') === 0) {
          group[this.getFormControlName(rule)] = new UntypedFormControl(rule.booleanValue);
        } else {
          group[this.getFormControlName(rule)] = new UntypedFormControl();
        }
      } else {
        if (this.ruleIsBool(rule)) {
          group[this.getFormControlName(rule)] = new UntypedFormControl(rule.booleanValue);
        } else {
          group[this.getFormControlName(rule)] = new UntypedFormControl(rule.value);
        }
      }
    }
    this.containerSettingsForm = this.formBuilder.group(group);
    this.subscribeToFormValueChanges();
  }

  private subscribeToFormValueChanges(): void {
    if (this.filteredRules && this.filteredRules.length > 0) {
      for (const rule of this.filteredRules) {
        const control = this.containerSettingsForm.controls[this.getFormControlName(rule)];
        control.valueChanges.pipe(takeUntil(this.componentIsDestroyed$)).subscribe((changes) => {
          let newValue = changes;
          if (this.ruleIsBool(rule)) {
            newValue = RuleHelper.getBooleanToString(changes);
          } else if (changes !== null) {
            newValue = changes.toString();
          }
          const dirty = this.isRulesDirty(this.filteredRules, rule, newValue);
          this.isDirty.next(dirty);
          this.isSaveEnabled.next(this.containerSettingsForm.valid && dirty);
        });
      }
    }
  }

  private sortList(items: RuleContainerSetting[]): RuleContainerSetting[] {
    return items.sort((item1, item2) => item1.rule.name.localeCompare(item2.rule.name));
  }

  private getEditWitnessPermission(): void {
    this.authorizationService.hasCurrentUserPermissions([InventoryManagementPermission.editWitnessRequired])
      .pipe(take(1))
      .subscribe(result => {
        this.hasEditWitnessPermission = result;
      });
  }
}
